import React, { useState, useMemo, useCallback } from 'react';
import Table from '../../components/Table/Table';
import CrewAndCarsDocumentForm from '../../components/HR/CrewAndCarsDocumentForm';
import { FilterBar, FilterConfig } from '../../components/Common';
import { CrewCertificate } from '../../types';
import { useCrewCertificates, useCreateCrewCertificate, useUpdateCrewCertificate, useDeleteCrewCertificate } from '../../hooks/useCrewCertificates';
import { useEmployees } from '../../hooks/useEmployees';
import DocumentDetailsScreen from './DocumentDetailsScreen';
import DocumentStatusColumn from './DocumentStatusColumn';
import ExpiryStatusBadges from './ExpiryStatusBadges';
import { getCertificateExpiryStatus, getExpiryStats, handleExportDocuments, handlePrintDocuments } from './documentHelpers';

const CrewAndCarsDocuments: React.FC = () => {
  const queryOptions = useMemo(() => ({
    sort: [{ field: 'createdAt', order: 'desc' as const }],
    pagination: { pageSize: 1000 }
  }), []);

  const { data: certificates = [], isLoading, error, refetch } = useCrewCertificates(queryOptions);
  const { data: employees = [] } = useEmployees(queryOptions);
  const createMutation = useCreateCrewCertificate(() => refetch());
  const updateMutation = useUpdateCrewCertificate(() => refetch());
  const deleteMutation = useDeleteCrewCertificate(() => refetch());

  const [showForm, setShowForm] = useState(false);
  const [editingCertificate, setEditingCertificate] = useState<CrewCertificate | undefined>();
  const [viewingDocuments, setViewingDocuments] = useState<CrewCertificate | null>(null);
  const [showDocumentScreen, setShowDocumentScreen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [courseTypeFilter, setCourseTypeFilter] = useState<string>('all');
  const [professionFilter, setProfessionFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [rankFilter, setRankFilter] = useState<string>('all');
  const [expiryFilter, setExpiryFilter] = useState<string>('all');
  const [dateFromFilter, setDateFromFilter] = useState<string>('');
  const [dateToFilter, setDateToFilter] = useState<string>('');

  const today = new Date();

  const getEmployeeName = useCallback((id: string) => employees.find(e => e.id === id)?.name || '-', [employees]);
  const getEmployeeCode = useCallback((id: string) => employees.find(e => e.id === id)?.code || '-', [employees]);
  const getEmployeeRank = useCallback((id: string) => employees.find(e => e.id === id)?.rank || '-', [employees]);

  const uniqueCourseTypes = Array.from(new Set(certificates.map(c => c.courseType).filter(Boolean)));
  const uniqueProfessions = Array.from(new Set(certificates.map(c => c.profession).filter((p): p is string => !!p)));
  const uniqueRanks = Array.from(new Set(employees.map(e => e.rank).filter(Boolean)));

  const filteredCertificates = useMemo(() => {
    return certificates.filter(cert => {
      const empName = getEmployeeName(cert.employeeId!);
      const empCode = getEmployeeCode(cert.employeeId!);
      const empRank = getEmployeeRank(cert.employeeId!);
      const expiryStatus = getCertificateExpiryStatus(cert);

      const matchesSearch = !searchTerm || [empName, empCode, cert.courseType, cert.profession || '', cert.certificateNumber, empRank]
        .some(v => v.toLowerCase().includes(searchTerm.toLowerCase()));

      return matchesSearch
        && (courseTypeFilter === 'all' || cert.courseType === courseTypeFilter)
        && (professionFilter === 'all' || cert.profession === professionFilter || !cert.profession)
        && (statusFilter === 'all' || cert.status === statusFilter)
        && (rankFilter === 'all' || empRank === rankFilter)
        && (expiryFilter === 'all' || expiryStatus === expiryFilter)
        && (!dateFromFilter || new Date(cert.issueDate) >= new Date(dateFromFilter))
        && (!dateToFilter || new Date(cert.issueDate) <= new Date(dateToFilter));
    });
  }, [certificates, searchTerm, courseTypeFilter, professionFilter, statusFilter, rankFilter, expiryFilter, dateFromFilter, dateToFilter, getEmployeeName, getEmployeeCode, getEmployeeRank]);

  if (isLoading) {
    return <div className="flex justify-center items-center h-64"><div className="text-gray-600">Loading documents...</div></div>;
  }
  if (error) {
    return <div className="flex justify-center items-center h-64"><div className="text-red-600">Error loading documents. Please try again.</div></div>;
  }

  const handleExport = () => handleExportDocuments(filteredCertificates, employees, getEmployeeCode, getEmployeeName, getEmployeeRank);
  const handlePrint = () => handlePrintDocuments(filteredCertificates, employees, getEmployeeName);

  const handleEdit = (cert: CrewCertificate) => { setEditingCertificate(cert); setShowForm(true); };
  const handleAdd = () => { setEditingCertificate(undefined); setShowForm(true); };
  const handleFormCancel = () => { setShowForm(false); setEditingCertificate(undefined); };

  const handleDelete = async (cert: CrewCertificate) => {
    if (window.confirm(`Are you sure you want to delete document ${cert.certificateNumber}?`)) {
      await deleteMutation.execute(cert.documentId || cert.id);
    }
  };

  const handleFormSubmit = async (data: Partial<CrewCertificate>) => {
    if (editingCertificate) {
      await updateMutation.execute({
        id: editingCertificate.documentId || editingCertificate.id,
        data: { ...data, documentId: editingCertificate.documentId }
      });
    } else {
      await createMutation.execute(data);
    }
    setShowForm(false);
    setEditingCertificate(undefined);
  };

  const { expiredCount, expiringSoonCount, totalDocuments } = getExpiryStats(filteredCertificates, employees);

  if (showForm) {
    return (
      <div className="space-y-6">
        <CrewAndCarsDocumentForm certificate={editingCertificate} onSubmit={handleFormSubmit} onCancel={handleFormCancel} />
      </div>
    );
  }

  if (showDocumentScreen && viewingDocuments) {
    const employee = employees.find((e: any) => e.id === viewingDocuments.employeeId!);
    return (
      <DocumentDetailsScreen
        employeeName={getEmployeeName(viewingDocuments.employeeId!)}
        employeeCode={getEmployeeCode(viewingDocuments.employeeId!)}
        employeeRank={getEmployeeRank(viewingDocuments.employeeId!)}
        documents={employee?.documents || []}
        onBack={() => { setViewingDocuments(null); setShowDocumentScreen(false); }}
      />
    );
  }

  const columns = [
    { key: 'employeeCode', header: 'Employee Code', label: 'Employee Code', sortable: true, render: (_: string, item: CrewCertificate) => getEmployeeCode(item.employeeId!) },
    { key: 'employeeName', header: 'Employee Name', label: 'Employee Name', sortable: true, render: (_: string, item: CrewCertificate) => getEmployeeName(item.employeeId!) },
    { key: 'rank', header: 'Rank', label: 'Rank', sortable: true, render: (_: string, item: CrewCertificate) => getEmployeeRank(item.employeeId!) },
    { key: 'validityStatus', header: 'Document Status', label: 'Document Status', sortable: false,
      render: (_: any, item: CrewCertificate) => <DocumentStatusColumn certificate={item} employees={employees} today={today} /> },
    { key: 'documents', header: 'Document Summary', label: 'Document Summary', sortable: false,
      render: (_: any, item: CrewCertificate) => (
        <button onClick={() => { setViewingDocuments(item); setShowDocumentScreen(true); }}
          className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500">
          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          View All Documents
        </button>
      ) }
  ];

  const filters: FilterConfig[] = [
    { key: 'courseType', label: 'Document Type', type: 'select', value: courseTypeFilter, onChange: setCourseTypeFilter,
      options: [{ value: 'all', label: 'All Document Types' }, ...uniqueCourseTypes.map(ct => ({ value: ct, label: ct }))] },
    { key: 'profession', label: 'Profession', type: 'select', value: professionFilter, onChange: setProfessionFilter,
      options: [{ value: 'all', label: 'All Professions' }, ...uniqueProfessions.map(p => ({ value: p, label: p }))] },
    { key: 'rank', label: 'Rank', type: 'select', value: rankFilter, onChange: setRankFilter,
      options: [{ value: 'all', label: 'All Ranks' }, ...uniqueRanks.map(r => ({ value: r, label: r }))] },
    { key: 'status', label: 'Status', type: 'select', value: statusFilter, onChange: setStatusFilter,
      options: [{ value: 'all', label: 'All Statuses' }, { value: 'valid', label: 'Valid' }, { value: 'expiring_soon', label: 'Expiring Soon' }, { value: 'expired', label: 'Expired' }] },
    { key: 'expiry', label: 'Expiry Status', type: 'select', value: expiryFilter, onChange: setExpiryFilter,
      options: [{ value: 'all', label: 'All' }, { value: 'valid', label: 'Valid' }, { value: 'expiring_soon', label: 'Expiring Soon' }, { value: 'expired', label: 'Expired' }] },
    { key: 'dateFrom', label: 'Issue Date From', type: 'date', value: dateFromFilter, onChange: setDateFromFilter },
    { key: 'dateTo', label: 'Issue Date To', type: 'date', value: dateToFilter, onChange: setDateToFilter },
  ];

  const clearFilters = () => {
    setSearchTerm(''); setCourseTypeFilter('all'); setProfessionFilter('all');
    setStatusFilter('all'); setRankFilter('all'); setExpiryFilter('all');
    setDateFromFilter(''); setDateToFilter('');
  };

  return (
    <div className="space-y-6">
      <div className="text-center space-y-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Crew and Cars Documents Management</h1>
          <p className="text-lg text-gray-600 mt-2">Manage crew and cars documents, training records, and compliance</p>
        </div>
        <ExpiryStatusBadges totalDocuments={totalDocuments} expiringSoonCount={expiringSoonCount} expiredCount={expiredCount} />
      </div>

      <FilterBar
        searchTerm={searchTerm} onSearchChange={setSearchTerm} searchPlaceholder="Search documents..."
        filters={filters} onClearFilters={clearFilters}
        totalCount={certificates.length} filteredCount={filteredCertificates.length}
        entityName="Crew and Cars Documents" onAdd={handleAdd} addLabel="Add Document"
        onExport={handleExport} onPrint={handlePrint}
      />

      <Table columns={columns} data={filteredCertificates} onEdit={handleEdit} onDelete={handleDelete}
        title="Crew and Cars Documents" showAddButton={false} searchable={false} />
    </div>
  );
};

export default CrewAndCarsDocuments;
